import { useEffect, useMemo, useState } from "react";
import { api, productImage, TYPE_LABELS, CATEGORIES, categoryOf } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Copy, FileText, Sparkles, Megaphone, ExternalLink, Plus, Trash2, Link2, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";

const copy = (text, label) => {
  navigator.clipboard.writeText(text || "");
  toast.success(`${label} copied`);
};

const CopyBtn = ({ text, label, tid }) => (
  <button onClick={() => copy(text, label)} data-testid={tid}
    className="h-7 px-2 rounded text-[11px] font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-600 border border-zinc-200 flex items-center gap-1 transition-colors">
    <Copy className="w-3 h-3" /> Copy
  </button>
);

export default function MarketingStudio() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("seo");
  const [storeUrl, setStoreUrl] = useState(localStorage.getItem("honorifica_store_url") || "https://honorifica.com");
  const [keyword, setKeyword] = useState("");
  const [busy, setBusy] = useState(false);
  const [seo, setSeo] = useState(null);
  const [pin, setPin] = useState(null);
  const [usps, setUsps] = useState(null);
  const [batching, setBatching] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [batchMode, setBatchMode] = useState("both");
  const [withContentOnly, setWithContentOnly] = useState(false);
  const [perDay, setPerDay] = useState(4);
  const [onlyNew, setOnlyNew] = useState(true);
  const [showPinFix, setShowPinFix] = useState(false);
  const [pinFixSel, setPinFixSel] = useState({});
  const [pinFixRequeue, setPinFixRequeue] = useState(true);
  const [pinFixBusy, setPinFixBusy] = useState(false);
  const DEFAULT_BOARD = "Meaningful Gift Ideas";
  const [boardName, setBoardName] = useState(localStorage.getItem("honorifica_pin_board") || DEFAULT_BOARD);
  const [boards, setBoards] = useState(() => {
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem("honorifica_pin_boards") || "[]"); } catch { saved = []; }
    if (!saved.includes(DEFAULT_BOARD)) saved = [DEFAULT_BOARD, ...saved];
    return saved;
  });

  const loadProducts = async () => {
    const { data } = await api.get("/products?workflow_state=published");
    setProducts(data);
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await loadProducts();
        if (data.length) setSelected(data[0]);
      } catch { toast.error("Could not load products"); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => { localStorage.setItem("honorifica_store_url", storeUrl); }, [storeUrl]);
  useEffect(() => { localStorage.setItem("honorifica_pin_board", boardName); }, [boardName]);
  useEffect(() => { localStorage.setItem("honorifica_pin_boards", JSON.stringify(boards)); }, [boards]);

  const addBoard = () => {
    const name = (window.prompt("Add a Pinterest board name (must match a board on your Pinterest):") || "").trim();
    if (!name) return;
    setBoards((b) => (b.includes(name) ? b : [...b, name]));
    setBoardName(name);
  };
  const removeBoard = () => {
    if (!boardName) return;
    if (!window.confirm(`Remove saved board "${boardName}"?`)) return;
    setBoards((b) => b.filter((x) => x !== boardName));
    setBoardName("");
  };
  useEffect(() => {
    setSeo(selected?.marketing?.seo || null);
    setPin(selected?.marketing?.pinterest || null);
    setUsps(selected?.marketing?.rangeme_usps || null);
    setKeyword("");
  }, [selected]);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return products.filter((p) => {
      if (withContentOnly && !(p.marketing?.seo || p.marketing?.pinterest)) return false;
      return (p.listing_title || "").toLowerCase().includes(t) || (p.product_type || "").includes(t);
    });
  }, [products, q, withContentOnly]);

  const PERSONALIZE_RE = /personaliz|monogram|add your|add a name|add their|your name|your pet|your photo|your text|your coordinates|with your|their name|custom\s+(name|photo|text|coordinates|map|portrait|star|date|veteran|printed)|custom printed|with name|with photo|name\s*&\s*dates|name and dates|upload|coordinates/i;
  const isPinnable = (p) => {
    if (p.mode === "personalized" || p.personalization_required === true) return false;
    if (p.customization_fields && p.customization_fields.length) return false;
    const text = [p.listing_title, p.title, p.concept, p.marketing?.pinterest?.kit?.pin_description]
      .filter(Boolean).join(" ");
    return !PERSONALIZE_RE.test(text);
  };
  const pinnable = useMemo(
    () => products.filter((p) => p.marketing?.pinterest && isPinnable(p)),
    [products]
  );
  const inCategory = (p) => !CATEGORIES.includes(boardName) || categoryOf(p) === boardName;
  const pinsCount = useMemo(() => pinnable.filter(inCategory).length, [pinnable, boardName]);
  const newCount = useMemo(
    () => pinnable.filter((p) => inCategory(p) && !p.marketing.pinterest.exported_at).length,
    [pinnable, boardName]
  );
  const exportCount = onlyNew ? newCount : pinsCount;

  const exportCsv = async (fmt = "standard") => {
    if (exportCount === 0) { toast.info(onlyNew ? "No new pins to export" : "No pins to export"); return; }
    try {
      const tzMin = -new Date().getTimezoneOffset();
      const sign = tzMin >= 0 ? "+" : "-";
      const abs = Math.abs(tzMin);
      const tz = `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
      const byCategory = boardName === "__by_category__";
      const isCat = CATEGORIES.includes(boardName);
      const params = new URLSearchParams({
        image_base: window.location.origin, fmt,
        per_day: String(perDay), start_hour: "9", tz_offset: tz,
        board: byCategory ? "" : boardName.trim(),
        by_category: byCategory ? "true" : "false",
        category: isCat ? boardName : "",
        only_new: onlyNew ? "true" : "false",
        mark_exported: onlyNew ? "true" : "false",
      });
      const { data } = await api.get(`/marketing/pins/export.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url; a.download = fmt === "publer" ? `honorifica-publer-v2-${stamp}.csv` : `honorifica-pins-${stamp}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportCount} pin(s)${fmt === "publer" ? ` · scheduled ${perDay}/day` : ""}`);
      if (onlyNew) await loadProducts();
    } catch { toast.error("Could not export CSV"); }
  };

  const resetExportHistory = async () => {
    if (!window.confirm("Mark all pins as new again? Your next export will include every pin (may create duplicates in Publer if already scheduled).")) return;
    try {
      const { data } = await api.post("/marketing/pins/reset-export");
      toast.success(`Reset — ${data.reset} pin(s) now count as new`);
      await loadProducts();
    } catch { toast.error("Could not reset"); }
  };

  const fixLinks = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/pins/fix-urls", { store_url: storeUrl });
      const fresh = await loadProducts();
      if (selected) {
        const cur = fresh.find((x) => x.id === selected.id);
        if (cur) setSelected(cur);
      }
      if (data.unmatched > 0) {
        toast.warning(`Fixed ${data.fixed} link(s) · ${data.unmatched} couldn't be matched to a live product`);
      } else {
        toast.success(`Fixed all ${data.fixed} product link(s) to live store URLs`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fix links — is the store live at that URL?");
    } finally { setBusy(false); }
  };

  const applyPinFix = async () => {
    const ids = Object.keys(pinFixSel).filter((k) => pinFixSel[k]);
    if (ids.length === 0) { toast.info("Pick at least one product to fix."); return; }
    setPinFixBusy(true);
    try {
      const { data } = await api.post("/marketing/pins/refresh-images", { product_ids: ids, requeue: pinFixRequeue });
      await loadProducts();
      if (data.updated > 0) {
        toast.success(`Updated ${data.updated} pin image(s) to real product photos${pinFixRequeue ? ` · re-queued ${data.requeued}` : ""}. ${pinFixRequeue ? "Re-export the Publer CSV (New only) to send them." : ""}`);
      } else {
        toast.warning(`No images updated (${data.no_listing} not published to Printify).${pinFixRequeue ? ` Re-queued ${data.requeued}.` : ""}`);
      }
      setShowPinFix(false);
      setPinFixSel({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh pin images");
    } finally { setPinFixBusy(false); }
  };

  const runBatch = async () => {
    if (!window.confirm(`Generate ${batchMode === "both" ? "SEO pages + Pinterest kits" : batchMode === "seo" ? "SEO pages" : "Pinterest kits"} for all published products? This only creates content in Heartline — nothing is posted anywhere. Existing content is skipped.`)) return;
    setBatching(true);
    setBatchProgress(null);
    try {
      const { data } = await api.post("/marketing/batch", { store_url: storeUrl, mode: batchMode, regenerate: false });
      toast.info(`Generating content for ${data.count} product(s)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setBatchProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error"].includes(j.status)) {
            clearInterval(poll);
            setBatching(false);
            if (j.status === "done") {
              toast.success(`Done — ${j.result?.seo || 0} SEO pages, ${j.result?.pinterest || 0} pins${j.result?.failed ? ` · ${j.result.failed} failed` : ""}`);
              const data2 = await loadProducts();
              const cur = data2.find((x) => x.id === selected?.id);
              if (cur) setSelected(cur);
            } else toast.error("Batch hit an error — check logs");
          }
        } catch { clearInterval(poll); setBatching(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start batch");
      setBatching(false);
    }
  };

  const genSeo = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/seo/${selected.id}`, { store_url: storeUrl, keyword });
      setSeo(data);
      toast.success("SEO page generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const genPin = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/pinterest/${selected.id}`, { store_url: storeUrl });
      setPin(data);
      toast.success("Pinterest kit generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const genUsps = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${selected.id}/rangeme-usps`);
      setUsps(data.usps);
      toast.success("USPs generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C05A45]" /></div>;

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="marketing-studio-page">
      <div className="mb-5">
        <p className="overline text-[#C05A45]">Advertise Honorifica · no integrations needed</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Marketing Studio</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">Turn any product into ready-to-post content — an SEO gift page for Google/your blog, and a paste-&-post Pinterest pin. No catalog sync or channel approval required; everything links straight to your store.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-3 mb-4 flex items-end gap-3 flex-wrap" data-testid="batch-bar">
        <label className="text-xs text-zinc-500">Store URL
          <Input value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} data-testid="store-url-input" className="mt-1 h-9 w-56" placeholder="https://honorifica.com" />
        </label>
        <label className="text-xs text-zinc-500">Generate
          <select value={batchMode} onChange={(e) => setBatchMode(e.target.value)} data-testid="batch-mode" className="mt-1 h-9 block bg-white border border-zinc-300 rounded-md px-2 text-sm outline-none focus:ring-2 focus:ring-[#C05A45]">
            <option value="both">SEO + Pinterest</option>
            <option value="seo">SEO pages only</option>
            <option value="pinterest">Pinterest kits only</option>
          </select>
        </label>
        <button onClick={runBatch} disabled={batching} data-testid="batch-generate-btn"
          className="h-9 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors">
          {batching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {batching ? `Generating… ${batchProgress ? `${batchProgress.done}/${batchProgress.total}` : ""}` : "Generate for all products"}
        </button>
        <span className="text-[11px] text-zinc-400 max-w-xs">Builds your content library. Nothing is posted — you pick which to pin afterward.</span>
        {pinnable.length > 0 && (
          <button onClick={fixLinks} disabled={busy} data-testid="fix-links-btn" title="Repair every pin's link to your real live store URLs"
            className="h-9 px-3 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50">
            <Link2 className="w-4 h-4" /> Fix links
          </button>
        )}
        {pinnable.length > 0 && (
          <button onClick={() => { setPinFixSel({}); setShowPinFix(true); }} disabled={busy} data-testid="fix-pin-images-btn" title="Replace selected products' pin images with their real Printify product photos, and optionally re-queue them into the drip feed"
            className="h-9 px-3 rounded-md border border-[#C05A45]/40 bg-[#C05A45]/5 hover:bg-[#C05A45]/10 text-[#C05A45] text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50">
            <ImageIcon className="w-4 h-4" /> Fix pin images
          </button>
        )}
        {pinnable.length > 0 && (
          <div className="flex items-end gap-2 ml-auto" data-testid="export-controls">
            <label className="text-xs text-zinc-500">Pinterest board
              <div className="mt-1 flex items-center gap-1">
                <select value={boardName} onChange={(e) => setBoardName(e.target.value)} data-testid="pin-board-select"
                  className="h-9 w-52 bg-white border border-zinc-300 rounded-md px-2 text-sm outline-none focus:ring-2 focus:ring-[#C05A45]">
                  <option value="__by_category__">By site category (auto)</option>
                  <optgroup label="One board for all pins">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                  {boards.length > 0 && (
                    <optgroup label="Custom boards">
                      {boards.map((b) => <option key={b} value={b}>{b}</option>)}
                    </optgroup>
                  )}
                  <option value="">Default board ({DEFAULT_BOARD})</option>
                </select>
                <button type="button" onClick={addBoard} data-testid="add-board-btn" title="Add custom board"
                  className="h-9 w-9 shrink-0 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 flex items-center justify-center transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
                {boards.includes(boardName) && (
                  <button type="button" onClick={removeBoard} data-testid="remove-board-btn" title="Remove custom board"
                    className="h-9 w-9 shrink-0 rounded-md border border-zinc-300 bg-white hover:bg-red-50 hover:text-red-600 text-zinc-500 flex items-center justify-center transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </label>
            <label className="text-xs text-zinc-500">Pins/day
              <Input type="number" min={1} max={25} value={perDay} data-testid="pins-per-day-input"
                onChange={(e) => setPerDay(Math.max(1, Math.min(25, Number(e.target.value) || 1)))}
                className="mt-1 h-9 w-16" />
            </label>
            <div className="flex flex-col justify-end pb-0.5">
              <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer select-none" title="Skip pins you've already exported/scheduled">
                <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)}
                  data-testid="only-new-toggle" className="accent-[#C05A45] w-3.5 h-3.5" />
                Only new pins
              </label>
              <span className="text-[10px] text-zinc-400 mt-0.5" data-testid="export-count-hint">
                {onlyNew ? `${newCount} new · ${pinsCount - newCount} already exported` : `${pinsCount} total`}
                {pinsCount - newCount > 0 && (
                  <button onClick={resetExportHistory} data-testid="reset-export-btn"
                    className="ml-1 underline hover:text-zinc-600">reset</button>
                )}
              </span>
            </div>
            <button onClick={() => exportCsv("publer")} disabled={exportCount === 0} data-testid="export-pins-publer-btn"
              className="h-9 px-3 rounded-md bg-[#C05A45] hover:bg-[#a94b39] text-white text-sm font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <FileText className="w-4 h-4" /> Publer CSV ({exportCount})
            </button>
            <button onClick={() => exportCsv("standard")} disabled={exportCount === 0} data-testid="export-pins-csv-btn"
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <FileText className="w-4 h-4" /> Standard CSV
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4">
        {/* Product picker */}
        <div className="bg-white border border-zinc-200 rounded-lg p-3 h-fit lg:sticky lg:top-4" data-testid="product-picker">
          <div className="relative mb-2">
            <Search className="w-4 h-4 text-zinc-400 absolute left-2 top-2.5" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-search" placeholder="Search products…" className="pl-8 h-9" />
          </div>
          <button onClick={() => setWithContentOnly((v) => !v)} data-testid="filter-with-content"
            className={`w-full mb-2 h-7 rounded text-[11px] font-medium border transition-colors ${withContentOnly ? "bg-[#C05A45] border-[#C05A45] text-white" : "bg-white border-zinc-300 text-zinc-500 hover:bg-zinc-50"}`}>
            {withContentOnly ? "Showing products with content" : "Show only products with content"}
          </button>
          <div className="max-h-[60vh] overflow-y-auto space-y-1">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => setSelected(p)} data-testid={`pick-${p.id}`}
                className={`w-full flex items-center gap-2 p-2 rounded-md text-left transition-colors ${selected?.id === p.id ? "bg-[#C05A45]/10 ring-1 ring-[#C05A45]/30" : "hover:bg-zinc-50"}`}>
                <img src={productImage(p)} alt="" className="w-10 h-10 rounded object-cover bg-zinc-100 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{p.listing_title}</div>
                  <div className="text-[10px] text-zinc-400">{TYPE_LABELS[p.product_type] || p.product_type}</div>
                </div>
                <div className="flex flex-col gap-0.5 shrink-0">
                  {p.marketing?.seo && <span className="text-[9px] bg-blue-50 text-blue-600 border border-blue-200 rounded px-1" title="SEO page generated">SEO</span>}
                  {p.marketing?.pinterest && <span className="text-[9px] bg-rose-50 text-rose-600 border border-rose-200 rounded px-1" title="Pin kit generated">Pin</span>}
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="text-xs text-zinc-400 text-center py-6">No matching products.</div>}
          </div>
        </div>

        {/* Content area */}
        <div className="min-w-0">
          {!selected ? (
            <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-sm text-zinc-400">Select a product to generate marketing content.</div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 p-4 border-b border-zinc-100">
                <img src={productImage(selected)} alt="" className="w-12 h-12 rounded object-cover bg-zinc-100" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{selected.listing_title}</div>
                  <div className="text-[11px] text-zinc-400">{TYPE_LABELS[selected.product_type] || selected.product_type}</div>
                </div>
              </div>

              <div className="flex border-b border-zinc-100">
                {[["seo", "SEO Page", FileText], ["pinterest", "Pinterest Kit", Megaphone], ["rangeme", "RangeMe USPs", Link2]].map(([k, label, Icon]) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#C05A45] text-[#C05A45]" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {tab === "rangeme" ? (
                  <div data-testid="rangeme-panel">
                    <p className="text-xs text-zinc-500 mb-3 max-w-2xl">Selling-point bullets for your RangeMe wholesale listing. "Veteran Owned" is always included; the rest are tailored to this product. Generate, then copy them into RangeMe's USP fields. <span className="text-zinc-400">(Country, State &amp; Case Pack are one-time settings in RangeMe's "Common Product Details" — no need to set them here.)</span></p>
                    <button onClick={genUsps} disabled={busy} data-testid="generate-usps-btn"
                      className="h-9 px-4 rounded-md bg-[#C05A45] hover:bg-[#a94a38] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors mb-4">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {usps ? "Regenerate USPs" : "Generate USPs"}
                    </button>
                    {usps && (
                      <div className="space-y-2" data-testid="usps-result">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-zinc-700">Unique Selling Propositions</div>
                          <CopyBtn text={usps.join("\n")} label="All USPs" tid="copy-usps-all" />
                        </div>
                        {usps.map((u, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 bg-zinc-50 rounded-md p-2.5" data-testid={`usp-row-${i}`}>
                            <div className="text-sm text-zinc-700 break-words">• {u}</div>
                            <CopyBtn text={u} label="USP" tid={`copy-usp-${i}`} />
                          </div>
                        ))}
                        <p className="text-[11px] text-zinc-400">Use "Copy all" to grab the full list, or copy each bullet individually.</p>
                      </div>
                    )}
                  </div>
                ) : tab === "seo" ? (
                  <div>
                    <div className="flex items-end gap-2 mb-4 flex-wrap">
                      <label className="text-xs text-zinc-500 flex-1 min-w-[220px]">Target keyword (optional)
                        <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} data-testid="seo-keyword-input" placeholder="e.g. memorial gift for dad" className="mt-1 h-9" />
                      </label>
                      <button onClick={genSeo} disabled={busy} data-testid="generate-seo-btn"
                        className="h-9 px-4 rounded-md bg-[#C05A45] hover:bg-[#a94a38] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {seo ? "Regenerate" : "Generate SEO page"}
                      </button>
                    </div>
                    {seo && (
                      <div className="space-y-3" data-testid="seo-result">
                        {[["Page title", seo.content.title, "copy-seo-title"], ["Meta description", seo.content.meta_description, "copy-seo-meta"], ["URL slug", seo.content.slug, "copy-seo-slug"]].map(([label, val, tid]) => (
                          <div key={tid} className="flex items-start justify-between gap-2 bg-zinc-50 rounded-md p-2.5">
                            <div className="min-w-0"><div className="text-[10px] uppercase text-zinc-400">{label}</div><div className="text-sm text-zinc-700 break-words">{val}</div></div>
                            <CopyBtn text={val} label={label} tid={tid} />
                          </div>
                        ))}
                        <div className="bg-zinc-50 rounded-md p-2.5">
                          <div className="text-[10px] uppercase text-zinc-400 mb-1">Keywords</div>
                          <div className="flex flex-wrap gap-1">{(seo.content.keywords || []).map((k, i) => <span key={i} className="text-[11px] bg-white border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">{k}</span>)}</div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-zinc-700">Page preview</div>
                          <CopyBtn text={seo.html} label="Page HTML" tid="copy-seo-html" />
                        </div>
                        <div className="border border-zinc-200 rounded-md overflow-hidden">
                          <iframe title="seo-preview" srcDoc={seo.html} data-testid="seo-preview-frame" className="w-full h-[420px] bg-white" />
                        </div>
                        <p className="text-[11px] text-zinc-400">Paste the copied HTML into a Shopify blog post, or a page's <strong>Custom Liquid</strong> section. It links to <a href={seo.url} target="_blank" rel="noreferrer" className="text-[#C05A45] underline">{seo.url}</a>.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <button onClick={genPin} disabled={busy} data-testid="generate-pin-btn"
                      className="h-9 px-4 rounded-md bg-[#C05A45] hover:bg-[#a94a38] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors mb-4">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {pin ? "Regenerate" : "Generate Pinterest kit"}
                    </button>
                    {pin && (
                      <div className="grid sm:grid-cols-[220px,1fr] gap-4" data-testid="pin-result">
                        <div>
                          <img src={productImage(selected)} alt={pin.kit.alt_text} className="w-full rounded-lg border border-zinc-200 object-cover" />
                          <p className="text-[10px] text-zinc-400 mt-1">Use this as your pin image (ideal 1000×1500).</p>
                        </div>
                        <div className="space-y-3">
                          {[["Pin title", pin.kit.pin_title, "copy-pin-title"], ["Description", pin.kit.pin_description, "copy-pin-desc"], ["Alt text", pin.kit.alt_text, "copy-pin-alt"], ["Destination URL", pin.url, "copy-pin-url"], ["Board", pin.kit.board_suggestion, "copy-pin-board"]].map(([label, val, tid]) => (
                            <div key={tid} className="flex items-start justify-between gap-2 bg-zinc-50 rounded-md p-2.5">
                              <div className="min-w-0"><div className="text-[10px] uppercase text-zinc-400">{label}</div><div className="text-sm text-zinc-700 break-words">{val}</div></div>
                              <CopyBtn text={val} label={label} tid={tid} />
                            </div>
                          ))}
                          <div className="flex items-start justify-between gap-2 bg-zinc-50 rounded-md p-2.5">
                            <div className="min-w-0"><div className="text-[10px] uppercase text-zinc-400">Hashtags</div>
                              <div className="flex flex-wrap gap-1 mt-1">{(pin.kit.hashtags || []).map((h, i) => <span key={i} className="text-[11px] bg-white border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">#{h}</span>)}</div>
                            </div>
                            <CopyBtn text={(pin.kit.hashtags || []).map((h) => `#${h}`).join(" ")} label="Hashtags" tid="copy-pin-hashtags" />
                          </div>
                        </div>
                      </div>
                    )}
                    {pin && (
                      <p className="text-[11px] text-zinc-400 mt-3 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> On Pinterest: Create Pin → upload the image → paste title/description/link. No catalog or merchant approval needed.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showPinFix && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="pin-fix-modal" onClick={() => !pinFixBusy && setShowPinFix(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-100">
              <h3 className="font-bold text-base flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#C05A45]" /> Fix pin images</h3>
              <p className="text-xs text-zinc-500 mt-1">Pick the products whose pins should use the <strong>real Printify product photo</strong> (the clean image on your Shopify listing). Only the ones you check are touched — your other pins are left alone.</p>
            </div>
            <div className="px-4 py-2 flex items-center justify-between text-xs">
              <div className="flex gap-3">
                <button onClick={() => setPinFixSel(Object.fromEntries(pinnable.map((p) => [p.id, true])))} className="text-[#C05A45] hover:underline" data-testid="pin-fix-select-all">Select all ({pinnable.length})</button>
                <button onClick={() => setPinFixSel(Object.fromEntries(pinnable.filter((p) => p.product_type === "totebag").map((p) => [p.id, true])))} className="text-[#C05A45] hover:underline" data-testid="pin-fix-select-totes">Just totes</button>
                <button onClick={() => setPinFixSel({})} className="text-zinc-500 hover:underline" data-testid="pin-fix-clear">Clear</button>
              </div>
              <span className="text-zinc-400">{Object.values(pinFixSel).filter(Boolean).length} selected</span>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-1 divide-y divide-zinc-50">
              {pinnable.map((p) => (
                <label key={p.id} className="flex items-center gap-3 py-2 cursor-pointer" data-testid={`pin-fix-row-${p.id}`}>
                  <input type="checkbox" checked={!!pinFixSel[p.id]} onChange={(e) => setPinFixSel((s) => ({ ...s, [p.id]: e.target.checked }))} className="accent-[#C05A45] w-4 h-4" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-800 truncate">{p.listing_title || "Untitled"}</div>
                    <div className="text-[11px] text-zinc-400">{p.product_type}{(p.channels?.printify?.listing_id) ? "" : " · not on Printify"}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="p-4 border-t border-zinc-100 space-y-3">
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer" data-testid="pin-fix-requeue">
                <input type="checkbox" checked={pinFixRequeue} onChange={(e) => setPinFixRequeue(e.target.checked)} className="accent-[#C05A45] w-4 h-4" />
                Re-queue into the drip feed (include in the next "New only" export)
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowPinFix(false)} disabled={pinFixBusy} className="h-9 px-4 rounded-md border border-zinc-300 text-sm text-zinc-600 hover:bg-zinc-50">Cancel</button>
                <button onClick={applyPinFix} disabled={pinFixBusy} data-testid="pin-fix-apply" className="h-9 px-4 rounded-md bg-[#C05A45] hover:bg-[#a94a37] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
                  {pinFixBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                  Update selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
